/**
 * Publish orchestration (Wave 3, Task 3.4) — the testable core behind the UI.
 * Filters the selection to publishable design frames, assembles them into a
 * static site (assemblePage), serializes that to the upload payload, and POSTs
 * it to the active project's publish endpoint.
 */
import type { CanvasDoc } from '../store/types';
import { assemblePage, type AssembledPage } from './assemblePage';
import type { PublishUpload, PublishedSite } from '../projects/api';

/** Selection ids that can be published: html design frames (free/main/instance).
 *  TSX + tool frames are out of scope for v1 publish. */
export function publishableFrameIds(selection: string[], doc: CanvasDoc): string[] {
  const byId = new Map(doc.frames.map((f) => [f.id, f] as const));
  return selection.filter((id) => {
    const f = byId.get(id);
    return !!f && (f.kind === 'free' || f.kind === 'main' || f.kind === 'instance');
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/** Serialize an assembled page to the JSON upload payload (Blobs → base64). */
export async function buildPublishRequest(assembled: AssembledPage, slug?: string): Promise<PublishUpload> {
  const files: Record<string, string> = {};
  for (const [path, content] of assembled.files) files[path] = content;

  const assets: Record<string, string> = {};
  for (const [path, blob] of assembled.assets) {
    assets[path] = bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
  }

  const req: PublishUpload = { files };
  if (slug != null) req.slug = slug;
  if (Object.keys(assets).length) req.assets = assets;
  return req;
}

export interface PublishSelectionDeps {
  projectId: string | null;
  doc: CanvasDoc;
  selection: string[];
  /** Assemble seam — defaults to the real browser assembler. */
  assemble?: (frameIds: string[], doc: CanvasDoc) => Promise<AssembledPage>;
  /** Upload seam — the projects API `publish(id, req)`. */
  publish: (id: string, req: PublishUpload) => Promise<PublishedSite>;
}

export async function publishSelection(deps: PublishSelectionDeps, slug?: string): Promise<PublishedSite> {
  if (!deps.projectId) throw new Error('No active project to publish to.');
  const frameIds = publishableFrameIds(deps.selection, deps.doc);
  if (frameIds.length === 0) throw new Error('Select one or more design frames to publish.');

  const assemble = deps.assemble ?? assemblePage;
  const assembled = await assemble(frameIds, deps.doc);
  const req = await buildPublishRequest(assembled, slug);
  return deps.publish(deps.projectId, req);
}
